export enum QueryKeys {
  VAULT_SCOPE = 'VAULT_SCOPE',
  VAULT = 'VAULT',
  VAULT_BLOCKCHAIN = 'VAULT_BLOCKCHAIN',
  VAULT_STATS = 'VAULT_STATS',
  VAULT_LIST = 'VAULT_LIST',
  VAULT_LIST_BLOCKCHAIN = 'VAULT_LIST_BLOCKCHAIN',
  VAULT_ALLOCATION = 'VAULT_ALLOCATION',
  VAULT_TRANSACTIONS = 'VAULT_TRANSACTIONS',
  VAULT_ORDER_REBALANCE_ESTIMATE = 'VAULT_ORDER_REBALANCE_ESTIMATE',
  VAULT_ORDERS = 'VAULT_ORDERS',
  VAULT_PREVIEW_REBALANCE = 'VAULT_PREVIEW_REBALANCE',
  VAULT_ESTIMATE_SWAP = 'VAULT_ESTIMATE_SWAP',
  VAULT_TOKENS = 'VAULT_TOKENS',
  VAULT_ADDRESSES = 'VAULT_ADDRESSES',
  VAULT_TOTAL_SUPPLY = 'VAULT_TOTAL_SUPPLY',
  VAULT_CONVERT_TO_ASSETS = 'VAULT_CONVERT_TO_ASSETS',
  VAULT_PRICE_PER_SHARE = 'VAULT_PRICE_PER_SHARE',
  VAULT_RULES_ENGINE_INDICATIONS = 'VAULT_RULES_ENGINE_INDICATIONS',
  VAULT_AVAILABLE_TOKENS = 'VAULT_AVAILABLE_TOKENS',
  VAULT_MANAGEMENT_WALLET = 'VAULT_MANAGEMENT_WALLET',
  VAULT_USER_VAULTS = 'VAULT_USER_VAULTS',
  VAULT_USER_DEFINED_VAULTS = 'VAULT_USER_DEFINED_VAULTS',
  VAULT_USER_VAULT_BY_ID = 'VAULT_USER_VAULT_BY_ID',
  VAULT_DECISION_REASONING = 'VAULT_DECISION_REASONING',
  VAULT_DEPOSIT_TOKEN_PRICE = 'VAULT_DEPOSIT_TOKEN_PRICE',
  VAULT_ONCHAIN_DATA_LIST = 'VAULT_ONCHAIN_DATA_LIST',
  VAULT_ONCHAIN_DATA_BY_ADDRESS = 'VAULT_ONCHAIN_DATA_BY_ADDRESS',
  TOKEN_PRICE = 'TOKEN_TOKEN_PRICE',
  TOKEN_LIST_INFO = 'TOKEN_LIST_INFO',
  TOKEN_PRICES = 'TOKEN_PRICES',

  CHAT = 'CHAT',
  CHAT_THREADS_LIST = 'CHAT_THREADS_LIST',
  CHAT_THREADS_LIMITS = 'CHAT_THREADS_LIMITS',
  CHAT_THREAD_REACTION = 'CHAT_THREAD_REACTION',
  ALLOWANCE = 'ALLOWANCE',
  SPENDER_ALLOWANCE = 'SPENDER_ALLOWANCE',

  SREP_WALLET = 'SREP_WALLET',
  STAKING_SCOPE = 'STAKING_SCOPE',
  STAKING_PROJECT = 'STAKING_PROJECT',
  STAKING_POOL_LIST = 'STAKING_POOL_LIST',
  USER_TOTAL_STAKED_ASSETS = 'USER_TOTAL_STAKED_ASSETS',

  NATIVE_BALANCES = 'NATIVE_BALANCES',
}

export const queryKeys = {
  vaultScope: [QueryKeys.VAULT_SCOPE] as const,
  nativeBalances: (params: { address: string }) => [
    QueryKeys.NATIVE_BALANCES,
    params.address,
  ],
  vault: (params: { chainId: number; address: string }) => [
    QueryKeys.VAULT_SCOPE,
    QueryKeys.VAULT,
    params.address,
    params.chainId,
  ],
  vaultBlockchain: (params: { chainId: number; address: string }) => [
    QueryKeys.VAULT_SCOPE,
    QueryKeys.VAULT_BLOCKCHAIN,
    params.address,
    params.chainId,
  ],
  vaultOnChainDataList: (params: {
    chainId: number;
    addressList: `0x${string}`[];
    walletAddress?: `0x${string}`;
  }) => [
    QueryKeys.VAULT_SCOPE,
    QueryKeys.VAULT_ONCHAIN_DATA_LIST,
    params.addressList,
    params.walletAddress,
    params.chainId,
  ],
  vaultOnChainDataByAddress: (params: { chainId: number }) => [
    QueryKeys.VAULT_SCOPE,
    QueryKeys.VAULT_ONCHAIN_DATA_BY_ADDRESS,
    params.chainId,
  ],
  vaultList: (params: { walletAddress?: `0x${string}`; isAuth: boolean }) => [
    QueryKeys.VAULT_SCOPE,
    QueryKeys.VAULT_LIST,
    params.walletAddress,
    params.isAuth,
  ],
  vaultListBlockchain: (params: {
    walletAddress?: string;
    isAuth: boolean;
  }) => [
    QueryKeys.VAULT_SCOPE,
    QueryKeys.VAULT_LIST_BLOCKCHAIN,
    params.walletAddress,
    params.isAuth,
  ],
  vaultOrders: (params: { vaultChainId: number; vaultAddress: string }) => [
    QueryKeys.VAULT_SCOPE,
    QueryKeys.VAULT_ORDERS,
    params.vaultAddress,
    params.vaultChainId,
  ],
  vaultAllocation: (params: { chainId: number; address: string }) => [
    QueryKeys.VAULT_SCOPE,
    QueryKeys.VAULT_ALLOCATION,
    params.address,
    params.chainId,
  ],
  vaultEstimateSwap: (params: {
    vaultChainId: number;
    vaultAddress: string;
    tokenIn: `0x${string}`;
    tokenOut: `0x${string}`;
    orderAmount: string;
    slippage?: string;
  }) => [
    QueryKeys.VAULT_SCOPE,
    QueryKeys.VAULT_ESTIMATE_SWAP,
    params.vaultAddress,
    params.vaultChainId,
    params.tokenIn,
    params.tokenOut,
    params.orderAmount,
  ],
  vaultAddresses: (params: {
    vaultAddress: `0x${string}`;
    chainId: number;
  }) => [
    QueryKeys.VAULT_SCOPE,
    QueryKeys.VAULT_ADDRESSES,
    params.vaultAddress,
    params.chainId,
  ],
  vaultTokens: (params: { vaultChainId: number; vaultAddress: string }) => [
    QueryKeys.VAULT_SCOPE,
    QueryKeys.VAULT_TOKENS,
    params.vaultAddress,
    params.vaultChainId,
  ],
  vaultPreviewRebalance: (params: {
    vaultAddress: `0x${string}`;
    vaultManagerAddress: `0x${string}`;
  }) => [
    QueryKeys.VAULT_SCOPE,
    QueryKeys.VAULT_PREVIEW_REBALANCE,
    params.vaultAddress,
    params.vaultManagerAddress,
  ],
  threadLimits: (params: { walletAddress: `0x${string}` }) => [
    QueryKeys.CHAT,
    QueryKeys.CHAT_THREADS_LIMITS,
    params.walletAddress,
  ],
  threadList: (params: { walletAddress: `0x${string}` }) => [
    QueryKeys.CHAT,
    QueryKeys.CHAT_THREADS_LIST,
    params.walletAddress,
  ],
  threadReaction: (params: {
    walletAddress: `0x${string}`;
    threadId: string;
  }) => [
    QueryKeys.CHAT,
    QueryKeys.CHAT_THREAD_REACTION,
    params.walletAddress,
    params.threadId,
  ],
  vaultTotalSupply: (params: {
    vaultChainId: number;
    vaultAddress: string;
  }) => [
    QueryKeys.VAULT_SCOPE,
    QueryKeys.VAULT_TOTAL_SUPPLY,
    params.vaultAddress,
    params.vaultChainId,
  ],
  vaultPricePerShare: (params: {
    vaultChainId: number;
    vaultAddress: string;
  }) => [
    QueryKeys.VAULT_SCOPE,
    QueryKeys.VAULT_PRICE_PER_SHARE,
    params.vaultAddress,
    params.vaultChainId,
  ],
  rulesEngineIndications: () => [
    QueryKeys.VAULT_SCOPE,
    QueryKeys.VAULT_RULES_ENGINE_INDICATIONS,
  ],
  vaultAvailableTokens: () => [
    QueryKeys.VAULT_SCOPE,
    QueryKeys.VAULT_AVAILABLE_TOKENS,
  ],
  managementWallets: () => [
    QueryKeys.VAULT_SCOPE,
    QueryKeys.VAULT_MANAGEMENT_WALLET,
  ],
  userVaultById: (params: { vaultId: string; userAddress: `0x${string}` }) => [
    QueryKeys.VAULT_SCOPE,
    QueryKeys.VAULT_USER_VAULT_BY_ID,
    params.vaultId,
    params.userAddress,
  ],
  userVaults: (params: { userAddress: `0x${string}` }) => [
    QueryKeys.VAULT_SCOPE,
    QueryKeys.VAULT_USER_VAULTS,
    params.userAddress,
  ],
  userDefinedVaults: (params: { userAddress: `0x${string}` }) => [
    QueryKeys.VAULT_SCOPE,
    QueryKeys.VAULT_USER_DEFINED_VAULTS,
    params.userAddress,
  ],
  vaultDepositTokenPrice: (params: {
    vaultAddress: `0x${string}`;
    chainId: number;
  }) => [
    QueryKeys.VAULT_SCOPE,
    QueryKeys.VAULT_DEPOSIT_TOKEN_PRICE,
    params.vaultAddress,
    params.chainId,
  ],
  tokenPrice: (params: {
    tokenAddress: `0x${string}`;
    quoteAddress: `0x${string}`;
    chainId: number;
  }) => [
    QueryKeys.VAULT_SCOPE,
    QueryKeys.TOKEN_PRICE,
    params.tokenAddress,
    params.quoteAddress,
    params.chainId,
  ],
  vaultStats: (params: {
    chainId: number;
    address: string;
    fromTimestamp: number;
    price: boolean;
    tvl: boolean;
  }) => [
    QueryKeys.VAULT_SCOPE,
    QueryKeys.VAULT_STATS,
    params.address,
    params.chainId,
    params.fromTimestamp,
    params.price,
    params.tvl,
  ],
  vaultTransactions: (params: { chainId: number; address: string }) => [
    QueryKeys.VAULT_SCOPE,
    QueryKeys.VAULT_TRANSACTIONS,
    params.address,
    params.chainId,
  ],
  vaultDecisionReasoning: (params: { chainId: number; address: string }) => [
    QueryKeys.VAULT_SCOPE,
    QueryKeys.VAULT_DECISION_REASONING,
    params.address,
    params.chainId,
  ],
  allowance: (params: {
    tokenAddress: `0x${string}`;
    chainId: number;
    addressOwner: `0x${string}`;
    addressSpender: `0x${string}`;
  }) => [
    QueryKeys.ALLOWANCE,
    params.tokenAddress,
    params.chainId,
    params.addressOwner,
    params.addressSpender,
  ],
  spenderAllowance: (params: {
    contractAddress: `0x${string}`;
    ownerAddress: `0x${string}`;
    tokenAddress: `0x${string}`;
    chainId: number;
  }) => [
    QueryKeys.SPENDER_ALLOWANCE,
    params.contractAddress,
    params.ownerAddress,
    params.tokenAddress,
    params.chainId,
  ],
  approveAllowance: (params: {
    tokenAddress: `0x${string}`;
    account: `0x${string}`;
    spenderAddress: `0x${string}`;
    tokenAmount: string;
    chainId: number;
  }) => [
    QueryKeys.SPENDER_ALLOWANCE,
    params.tokenAddress,
    params.account,
    params.spenderAddress,
    params.tokenAmount,
    params.chainId,
  ],
  stakingProject: (params: { tokenSymbol: string }) => [
    QueryKeys.STAKING_SCOPE,
    QueryKeys.STAKING_PROJECT,
    params.tokenSymbol,
  ],
  stakingPoolList: (params: { tokenSymbol: string }) => [
    QueryKeys.STAKING_SCOPE,
    QueryKeys.STAKING_POOL_LIST,
    params.tokenSymbol,
  ],
  userTotalStakedAssets: (params: { userAddress: `0x${string}` }) => [
    QueryKeys.STAKING_SCOPE,
    QueryKeys.USER_TOTAL_STAKED_ASSETS,
    params.userAddress,
  ],
  tokenPrices: () => [QueryKeys.TOKEN_PRICES],
  tokenListInfo: (params: { chainId?: number; tokenList: `0x${string}`[] }) => [
    QueryKeys.TOKEN_LIST_INFO,
    params.chainId,
    params.tokenList,
  ],
  srepWallet: (params: {
    walletAddress: `0x${string}`;
    depositTokenSymbol: string;
  }) => [
    QueryKeys.STAKING_SCOPE,
    QueryKeys.SREP_WALLET,
    params.walletAddress,
    params.depositTokenSymbol || 'all',
  ],
};
